import React, { useRef, memo, useEffect, useContext } from 'react';
import styles from './index.module.scss';
import { getMallList, getIchibanList, getLotteryList } from '@/apis';
import { useMallParams } from '@/stores';
import type { ProductKindItem } from '@/stores';
import useUrlState from '@ahooksjs/use-url-state';
import { useQuery } from '@tanstack/react-query';
import ProductItem from '@/components/ProductItem';
import { Spin, Empty, Pagination, Button } from 'antd';
import { omit } from 'lodash';
import globalContext from '@/configs/globalContext';
import cls from 'classnames';

// 0：无、1：normal-一般商品、2：lottery-模坑赏、3：hexapus-一番赏
enum ProductType {
  normal = 1,
  lottery,
  hexapus,
}

export enum RewardType {
  /** 现货活动 */
  Default = 1,
  /** 预售活动 */
  Prepaid,
}

type MailListParams = Parameters<typeof getMallList>[0];

type QueryType = Omit<
  MailListParams,
  'ip_id' | 'type' | 'price__range' | 'page_size'
> & {
  ips?: string;
  price?: string;
  type?: string;
  factory?: string;
};

interface Props {
  classInfo?: ProductKindItem;
  isNormalProduct?: boolean;
}

const btnList = [
  {
    title: 'In stock',
    value: RewardType.Default,
    cls: styles.btn,
    active: styles.activeBtn,
  },
  {
    title: 'Pre-sale',
    value: RewardType.Prepaid,
    cls: styles.prebtn,
    active: styles.activePreBtn,
  },
  {
    title: 'All',
    cls: styles.allBtn,
    value: undefined,
  },
];

const ProductList: React.FC<Props> = ({ classInfo, isNormalProduct }) => {
  const class_id = classInfo?.id;
  const { isMobile } = useContext(globalContext);
  const [query, setQuery] = useUrlState<QueryType>({});
  const total = useRef(0);

  const selectedType = useRef<RewardType>(RewardType.Default);
  const { mallParams, updateMallParams } = useMallParams();

  let method = getMallList;
  if (classInfo) {
    if (classInfo.class_type === ProductType.lottery) {
      method = getLotteryList;
    } else if (classInfo.class_type === ProductType.hexapus) {
      method = getIchibanList;
    }
  }
  const {
    data: products,
    isFetching,
    refetch,
  } = useQuery(
    [method.name, class_id, mallParams],
    async () => {
      let params;
      let queryParams;
      if (isNormalProduct) {
        params = {
          // 全部商品class_id为0
          class_id: class_id === 0 ? undefined : class_id,
          // 如果是全部商品，过滤掉钻石商品 1为现金商品
          pay_channel: class_id === 0 ? 1 : undefined,
          ...mallParams,
        };
        queryParams = {
          ips: params.ip_id__in,
          price: params.price__range,
          factory: params.factory__in,
          ...omit(params, [
            'ip_id__in',
            'price__range',
            'class_id',
            'factory__in',
            'pay_channel',
          ]),
        };
      } else {
        params = {
          ...omit(mallParams, ['type']),
          // 这个type是预售、现货的意思，跟之前不一样
          type: selectedType.current,
        };
        queryParams = queryParams = {
          ips: params.ip_id__in,
          price: params.price__range,
          type: undefined,
          ...omit(params, [
            'ip_id__in',
            'price__range',
            'class_id',
            'factory__in',
            'type',
          ]),
        };
      }
      // console.log('请求参数', params);
      // console.log('url参数', queryParams);
      setQuery(queryParams);
      // console.log('请求参数', params, 'url参数', queryParams);
      const res = await method(params);
      total.current = res.data?.total ?? 0;
      return res.data?.list ?? [];
    },
    {
      enabled: !!class_id || class_id === 0,
    },
  );
  const changeType = (type: any) => {
    if (selectedType.current !== type) {
      selectedType.current = type;
      refetch();
    }
  };
  const handleChangePage = (page: number) => {
    updateMallParams(
      {
        page,
      },
      true,
    );
  };
  const totalPage = Math.ceil(total.current / (mallParams?.page_size ?? 50));
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 600);
  }, [products]);
  return (
    <div className={styles.container}>
      <Spin className={styles.loading} spinning={isFetching} size="large" />
      {classInfo &&
        (classInfo.class_type === ProductType.lottery ||
          classInfo.class_type === ProductType.hexapus) && (
          <div className={styles.btns}>
            {btnList.map((item) => (
              <Button
                className={cls(
                  item.cls,
                  item.active && {
                    [item.active]: selectedType.current === item.value,
                  },
                )}
                key={item.value}
                type={
                  selectedType.current === item.value ? 'primary' : 'default'
                }
                onClick={() => changeType(item.value)}
              >
                {item.title}
              </Button>
            ))}
          </div>
        )}
      <div className={styles.wrap}>
        {products &&
          products.length > 0 &&
          products.map((item) => (
            <div className={styles.mallItem} key={item.id}>
              <ProductItem
                info={{
                  ...item,
                  class_type: classInfo?.class_type,
                  classid: class_id,
                }}
              />
            </div>
          ))}
        {products && products.length === 0 && (
          <Empty style={{ paddingTop: '10%' }} />
        )}
      </div>
      {totalPage > 1 && (
        <div className={styles.foot}>
          <p>
            Page {query.page}, Total {totalPage}
          </p>
          <Pagination
            defaultPageSize={mallParams?.page_size}
            hideOnSinglePage={true}
            showSizeChanger={false}
            defaultCurrent={+query.page}
            current={+query.page}
            total={total.current}
            onChange={handleChangePage}
            size={isMobile ? 'small' : 'default'}
          />
        </div>
      )}
    </div>
  );
};

export default memo(ProductList);
